import React, { type FunctionComponent, useMemo, useState, useRef, useCallback } from 'react'
import { UploadIcon } from '@instacart/ids-core'
import { useFilestack } from '../../hooks/useFilestack.hooks'
import { Button } from '../../tds'
import { DropArea, FileInput, ButtonContainer } from './utils/fileUploader.styles'
import {
  type FileUploaderProps,
  type FileUploaderErrorTypes,
  type FileWithError,
} from './utils/fileUploader.types'
import { useDragAndDrop } from './utils/fileUploader.hooks'
import { type ButtonVariant } from './utils/fileUploader.types'

/**
 * File upload component with built-in support for drag and drop and uploading to Filestack.
 */
export const FileUploader: FunctionComponent<React.PropsWithChildren<FileUploaderProps>> = ({
  accept,
  currentUrl,
  uploadToFilestack = false,
  filestackApiKey = 'noupload',
  validateFile,
  onUpload,
  DropAreaComponent,
  droppedAreaComponentMessages,
  UploadButtonComponent,
  uploadButtonProps,
  DroppedFileComponent,
  ErrorComponent,
  errorComponentMessages,
  className,
  container,
  region,
  path,
  disabled = false,
  multiple = false,
  encodeFileName = false,
}) => {
  const [files, setFiles] = useState<FileWithError[]>([])
  const [errorType, setErrorType] = useState<FileUploaderErrorTypes | null>(null)
  const [highlightDropArea, setHighlightDropArea] = useState<boolean>(false)

  const { url, uploading, uploadProgress, uploadError, uploadFile } = useFilestack({
    value: null,
    filestackApiKey,
    onFileUpload: (uploadUrl: string, file: File, key) => onUpload(file, uploadUrl, key),
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropAreaRef = useRef<HTMLDivElement>(null)

  const handleFiles = useCallback(
    (newFiles: File[]) => {
      if (!multiple) {
        // If multiple is false, only take the first file
        const file = newFiles[0]
        if (!file) return

        setFiles([{ file, errorType: null }])
        setErrorType(null)

        if (validateFile) {
          const validationError = validateFile(file)
          if (validationError) {
            setErrorType(validationError)
            setFiles([{ file, errorType: validationError }])
            return
          }
        }

        const processedFile = encodeFileName
          ? new File([file], encodeURIComponent(file.name), { type: file.type })
          : file

        if (uploadToFilestack) {
          uploadFile(processedFile, container, region, path)
        } else {
          onUpload(processedFile)
        }
      } else {
        // Handle multiple files
        setErrorType(null)

        newFiles.forEach(file => {
          if (validateFile) {
            const validationError = validateFile(file)
            if (validationError) {
              setFiles(prevFiles => [...prevFiles, { file, errorType: validationError }])
              setErrorType(validationError)
              return
            }
          }
          setFiles(prevFiles => [...prevFiles, { file, errorType: null }])

          const processedFile = encodeFileName
            ? new File([file], encodeURIComponent(file.name), { type: file.type })
            : file

          if (uploadToFilestack) {
            uploadFile(processedFile, container, region, path)
          } else {
            onUpload(processedFile)
          }
        })
      }
    },
    [
      multiple,
      setFiles,
      setErrorType,
      validateFile,
      uploadToFilestack,
      uploadFile,
      onUpload,
      container,
      region,
      path,
      encodeFileName,
    ]
  )

  /** Called when files are dropped onto the drop area. */
  const handleFileDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      // e.stopPropagation()

      if (disabled) {
        return
      }

      const droppedFiles = Array.from(e.dataTransfer?.files || [])
      if (droppedFiles.length > 0) {
        handleFiles(droppedFiles)
      }
    },
    [handleFiles, disabled]
  )

  /** Called when files are selected from the system filepicker dialog. */
  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()

      if (disabled) {
        return
      }

      const selectedFiles = Array.from(e.target.files || [])
      if (selectedFiles.length > 0) {
        handleFiles(selectedFiles)
      }
    },
    [handleFiles, disabled]
  )

  const openFilePicker = useCallback(() => {
    if (disabled) {
      return
    }

    const fileInput = fileInputRef.current
    if (fileInput) {
      fileInput.value = ''
      fileInput.click()
    }
  }, [fileInputRef, disabled])

  /** Set up drag and drop functionality for the drop area. */
  useDragAndDrop({
    dropAreaRef,
    onDragStart: () => setHighlightDropArea(true),
    onDragEnd: () => setHighlightDropArea(false),
    onDrop: handleFileDrop,
  })

  const handleRemoveFile = useCallback(
    (fileToRemove: File) => {
      // can only remove files if not uploading to Filestack
      if (uploadToFilestack) return

      setFiles(prevFiles => prevFiles.filter(file => file.file !== fileToRemove))
    },
    [uploadToFilestack]
  )

  const content = useMemo(() => {
    if (currentUrl) {
      if (URL.canParse(currentUrl)) {
        const parsedUrl = new URL(currentUrl)
        const pathname = parsedUrl.pathname
        const filename = pathname.substring(pathname.lastIndexOf('/') + 1)

        return (
          <DroppedFileComponent
            fileName={filename}
            url={currentUrl}
            uploading={false}
            uploadProgress={0}
            files={files}
            onRemove={handleRemoveFile}
          />
        )
      } else {
        return (
          <DroppedFileComponent
            fileName={currentUrl}
            url={currentUrl}
            uploading={false}
            uploadProgress={0}
            files={files}
            onRemove={handleRemoveFile}
          />
        )
      }
    }

    if (files.length > 0) {
      if (errorType) {
        return (
          <ErrorComponent
            fileName={files[0].file.name}
            files={files}
            onRemove={handleRemoveFile}
            errorType={errorType}
            messages={errorComponentMessages}
          />
        )
      } else if (uploadError) {
        return (
          <ErrorComponent
            fileName={files[0].file.name}
            files={files}
            onRemove={handleRemoveFile}
            errorType="uploadError"
            messages={errorComponentMessages}
          />
        )
      }

      return (
        <DroppedFileComponent
          fileName={files[0].file.name}
          url={url}
          uploading={uploading}
          uploadProgress={uploadProgress}
          files={files}
          onRemove={handleRemoveFile}
        />
      )
    }

    return <DropAreaComponent messages={droppedAreaComponentMessages} />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    errorType,
    files,
    uploading,
    uploadProgress,
    uploadError,
    url,
    currentUrl,
    handleRemoveFile,
    uploadToFilestack,
  ])

  const isFilePresent = files.length > 0 || !!currentUrl

  let buttonVariant: ButtonVariant = 'primary'
  if (errorType && uploadButtonProps?.errorButton?.variant) {
    buttonVariant = uploadButtonProps.errorButton.variant
  } else if (isFilePresent && uploadButtonProps?.droppedFileButton?.variant) {
    buttonVariant = uploadButtonProps.droppedFileButton.variant
  } else if (uploadButtonProps?.dropAreaButton?.variant) {
    buttonVariant = uploadButtonProps.dropAreaButton.variant
  }

  let buttonText: string | undefined
  if (errorType) {
    buttonText = uploadButtonProps?.errorButton?.uploadButton
  } else if (isFilePresent) {
    buttonText = uploadButtonProps?.droppedFileButton?.uploadButton
  } else {
    buttonText = uploadButtonProps?.dropAreaButton?.uploadButton
  }

  return (
    <div>
      <DropArea
        ref={dropAreaRef}
        highlight={highlightDropArea}
        onClick={openFilePicker}
        className={className}
      >
        <FileInput
          data-testid="file-input"
          type="file"
          accept={accept}
          ref={fileInputRef}
          onChange={handleFileInput}
          disabled={disabled}
          multiple={multiple}
        />
        {content}
      </DropArea>
      <ButtonContainer>
        <Button
          leftIcon={UploadIcon}
          variant={buttonVariant}
          onClick={openFilePicker}
          disabled={disabled}
        >
          {buttonText}
        </Button>
      </ButtonContainer>
    </div>
  )
}
